import { useState, useRef } from 'react';
import clsx from 'clsx';
import {
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  XMarkIcon,
  BuildingStorefrontIcon,
  ArrowDownTrayIcon,
  LinkIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { storesApi, ordersApi, integrationsApi } from '../api/client';

type Platform = 'ubereats' | 'doordash' | 'grubhub';

interface PlatformConfig {
  id: Platform;
  name: string;
  color: string;
  portalUrl: string;
  disputeWindow: string;
  oauthAvailable: boolean;
  instructions: string[];
  csvFields: string[];
}

const PLATFORMS: PlatformConfig[] = [
  {
    id: 'ubereats',
    name: 'Uber Eats',
    color: '#06C167',
    portalUrl: 'merchants.ubereats.com',
    disputeWindow: '30 days',
    oauthAvailable: true, // OAuth credentials configured
    instructions: [
      'Log into Uber Eats Manager at merchants.ubereats.com',
      'Navigate to Reports > Request History',
      'Click "Request Reports" and select "Order Errors (Transaction)"',
      'Wait for report status to change to "Available"',
      'Download the CSV file (expires after 48 hours)',
    ],
    csvFields: ['Order ID', 'Order Date', 'Issue Type', 'Item Name', 'Customer Refund', 'Merchant Charge'],
  },
  {
    id: 'doordash',
    name: 'DoorDash',
    color: '#FF3008',
    portalUrl: 'merchant.doordash.com',
    disputeWindow: '14 days',
    oauthAvailable: false, // Pending OAuth setup
    instructions: [
      'Log into DoorDash Merchant Portal at merchant.doordash.com',
      'Navigate to Reports tab',
      'Click "Create Report" > Financial Report',
      'Check "Error Charges and adjustments" option',
      'Select date range and download CSV',
    ],
    csvFields: ['Order ID', 'Delivery Date', 'Subtotal', 'Error Charges', 'Adjustments', 'Net Total'],
  },
  {
    id: 'grubhub',
    name: 'Grubhub',
    color: '#F63440',
    portalUrl: 'restaurant.grubhub.com',
    disputeWindow: '30 days',
    oauthAvailable: false, // Requires business partnership - no self-service
    instructions: [
      'Log into Grubhub for Restaurants at restaurant.grubhub.com',
      'Navigate to Transactions section',
      'Select restaurant(s) and date range (up to 45 days)',
      'Filter by transaction type to include Adjustments',
      'Click "Download CSV"',
    ],
    csvFields: ['Order Number', 'Date', 'Time', 'Subtotal', 'Tax', 'Total', 'Adjustment Amount'],
  },
];

interface StoreData {
  name: string;
  platform: Platform;
  externalId: string;
  orderCount: number;
}

interface WizardState {
  step: number;
  selectedPlatforms: Platform[];
  uploadedFiles: Record<Platform, File | null>;
  parsedStores: StoreData[];
  importResults: {
    ordersImported: number;
    storesCreated: number;
    atRiskAmount: number;
  } | null;
}

interface SetupWizardProps {
  onComplete: () => void;
  onClose: () => void;
}

export default function SetupWizard({ onComplete, onClose }: SetupWizardProps) {
  const [state, setState] = useState<WizardState>({
    step: 1,
    selectedPlatforms: [],
    uploadedFiles: { ubereats: null, doordash: null, grubhub: null },
    parsedStores: [],
    importResults: null,
  });
  const [showCsvUpload, setShowCsvUpload] = useState<Record<Platform, boolean>>({
    ubereats: false,
    doordash: false,
    grubhub: false,
  });
  const [oauthConnecting, setOauthConnecting] = useState<Platform | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInputRefs = {
    ubereats: useRef<HTMLInputElement>(null),
    doordash: useRef<HTMLInputElement>(null),
    grubhub: useRef<HTMLInputElement>(null),
  };

  const togglePlatform = (platform: Platform) => {
    setState((prev) => ({
      ...prev,
      selectedPlatforms: prev.selectedPlatforms.includes(platform)
        ? prev.selectedPlatforms.filter((p) => p !== platform)
        : [...prev.selectedPlatforms, platform],
    }));
  };

  const handleOAuthConnect = async (platform: Platform) => {
    setOauthConnecting(platform);
    setError(null);
    
    try {
      const platformConfig = PLATFORMS.find(p => p.id === platform)!;
      const storeRes = await storesApi.create({
        name: `My ${platformConfig.name} Restaurant`,
        aggregator: platform,
        external_id: `pending-${Date.now()}`
      });
      
      const storeId = storeRes.data.id;
      const oauthRes = await integrationsApi.startOAuth(storeId, platform);
      window.location.href = oauthRes.data.redirect_url;
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || "OAuth connection failed. Please try CSV upload instead.";
      setError(message);
      setShowCsvUpload(prev => ({ ...prev, [platform]: true }));
    } finally {
      setOauthConnecting(null);
    }
  };


  const handleFileUpload = (platform: Platform, file: File | null) => {
    setState((prev) => ({
      ...prev,
      uploadedFiles: { ...prev.uploadedFiles, [platform]: file },
    }));
  };

  function parseCSV(text: string): Record<string, string>[] {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map((h) => h.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'));
    const rows: Record<string, string>[] = [];

    for (let i = 1; i < lines.length; i++) {
      // Handle quoted values with commas inside
      const values: string[] = [];
      let current = '';
      let inQuotes = false;

      for (const char of lines[i]) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim().replace(/^"|"$/g, ''));
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim().replace(/^"|"$/g, ''));

      const row: Record<string, string> = {};
      headers.forEach((header, idx) => {
        row[header] = values[idx] || '';
      });
      rows.push(row);
    }
    return rows;
  }

  const processUploads = async () => {
    setProcessing(true);
    setError(null);

    try {
      const stores: StoreData[] = [];
      const allOrders: { storeId: string; orders: unknown[] }[] = [];

      for (const platform of state.selectedPlatforms) {
        const file = state.uploadedFiles[platform];
        if (!file) continue;

        const text = await file.text();
        const rows = parseCSV(text);

        // Extract unique store identifiers and orders
        const storeOrders: Record<string, unknown[]> = {};

        for (const row of rows) {
          // Try to identify store from row
          const storeName = row.store_name || row.restaurant_name || row.location || PLATFORMS.find(p => p.id === platform)?.name || 'Unknown Store';
          const storeKey = `${platform}-${storeName}`;

          if (!storeOrders[storeKey]) {
            storeOrders[storeKey] = [];
            stores.push({
              name: storeName,
              platform,
              externalId: row.store_id || row.restaurant_id || '',
              orderCount: 0,
            });
          }

          // Map row to order format
          const order = {
            aggregator_order_id: row.order_id || row.order_number || `${platform.toUpperCase()}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            order_date: row.order_date || row.delivery_date || row.date || new Date().toISOString(),
            total_amount: parseFloat(row.total_amount || row.subtotal || row.total || '0'),
            items: [{ name: 'Imported Order', quantity: 1, price: parseFloat(row.total_amount || row.subtotal || row.total || '0') }],
            refund_amount: parseFloat(row.refund_amount || row.customer_refund || row.error_charges || row.adjustment_amount || '0'),
            refund_reason: row.refund_reason || row.issue_type || row.adjustment_type || undefined,
          };

          storeOrders[storeKey].push(order);
        }

        // Update store order counts
        for (const store of stores) {
          const key = `${store.platform}-${store.name}`;
          if (storeOrders[key]) {
            store.orderCount = storeOrders[key].length;
          }
        }

        // Prepare for import
        for (const [key, orders] of Object.entries(storeOrders)) {
          allOrders.push({ storeId: key, orders });
        }
      }

      setState((prev) => ({ ...prev, parsedStores: stores }));

      // Create stores and import orders
      let totalOrders = 0;
      let totalAtRisk = 0;

      for (const store of stores) {
        try {
          // Create store
          const storeRes = await storesApi.create({
            name: store.name,
            aggregator: store.platform,
            external_id: store.externalId || undefined,
          });
          const createdStoreId = storeRes.data.id;

          // Find orders for this store
          const storeKey = `${store.platform}-${store.name}`;
          const storeOrders = allOrders.find(o => o.storeId === storeKey)?.orders || [];

          if (storeOrders.length > 0) {
            // Import orders
            const importRes = await ordersApi.import(createdStoreId, storeOrders as never[]);
            totalOrders += importRes.data.imported || 0;

            // Calculate at-risk
            for (const order of storeOrders as { refund_amount?: number }[]) {
              if (order.refund_amount && order.refund_amount > 0) {
                totalAtRisk += order.refund_amount;
              }
            }
          }
        } catch (err) {
          console.error(`Failed to create store ${store.name}:`, err);
        }
      }

      setState((prev) => ({
        ...prev,
        importResults: {
          ordersImported: totalOrders,
          storesCreated: stores.length,
          atRiskAmount: totalAtRisk,
        },
        step: 4,
      }));
    } catch (err) {
      setError('Failed to process uploads. Please try again.');
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const downloadTemplate = (platform: Platform) => {
    const templates: Record<Platform, string> = {
      ubereats: `order_id,order_date,store_name,issue_type,item_name,total_amount,customer_refund,merchant_charge
UE-12345678,2024-01-15,Downtown Location,missing_item,Burger Combo,45.99,12.50,12.50
UE-87654321,2024-01-16,Airport Kiosk,incorrect_item,Salad Bowl,28.75,28.75,28.75`,
      doordash: `order_id,delivery_date,store_name,subtotal,error_charges,adjustments,net_total
DD-11223344,2024-01-15,Main Street,32.50,8.99,0,23.51
DD-55667788,2024-01-16,Mall Location,45.00,45.00,0,0`,
      grubhub: `order_number,date,time,store_name,subtotal,tax,total,adjustment_type,adjustment_amount
GH-99887766,2024-01-15,18:30,Central Kitchen,28.00,2.24,30.24,refund,15.00
GH-44332211,2024-01-16,12:00,West Side,52.50,4.20,56.70,adjustment,25.00`,
    };

    const blob = new Blob([templates[platform]], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${platform}_import_template.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const canProceed = () => {
    switch (state.step) {
      case 1:
        return state.selectedPlatforms.length > 0;
      case 2:
        return state.selectedPlatforms.every((p) => state.uploadedFiles[p] !== null);
      case 3:
        return true;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (state.step === 2) {
      processUploads();
    } else if (state.step === 3) {
      // Skip directly to import
      processUploads();
    } else {
      setState((prev) => ({ ...prev, step: prev.step + 1 }));
    }
  };

  const prevStep = () => {
    setState((prev) => ({ ...prev, step: Math.max(1, prev.step - 1) }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Connect Your Platforms</h2>
            <p className="text-sm text-slate-500 mt-0.5">5-minute setup to start recovering refunds</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center justify-between">
            {['Select Platforms', 'Upload Data', 'Review', 'Complete'].map((label, idx) => (
              <div key={label} className="flex items-center">
                <div
                  className={clsx(
                    'flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors',
                    state.step > idx + 1
                      ? 'bg-emerald-500 text-white'
                      : state.step === idx + 1
                      ? 'bg-amber-500 text-white'
                      : 'bg-slate-200 text-slate-500'
                  )}
                >
                  {state.step > idx + 1 ? <CheckCircleIcon className="w-5 h-5" /> : idx + 1}
                </div>
                <span
                  className={clsx(
                    'ml-2 text-sm hidden sm:block',
                    state.step === idx + 1 ? 'text-slate-900 font-medium' : 'text-slate-500'
                  )}
                >
                  {label}
                </span>
                {idx < 3 && (
                  <div className="w-8 sm:w-16 h-0.5 bg-slate-200 mx-2" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-xl text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Platform Selection */}
          {state.step === 1 && (
            <div className="space-y-4">
              <p className="text-slate-600">
                Select the delivery platforms you use. You can connect multiple platforms.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {PLATFORMS.map((platform) => (
                  <button
                    key={platform.id}
                    onClick={() => togglePlatform(platform.id)}
                    className={clsx(
                      'p-4 rounded-xl border-2 transition-all text-left',
                      state.selectedPlatforms.includes(platform.id)
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    )}
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                      style={{ backgroundColor: `${platform.color}20` }}
                    >
                      <span className="text-lg font-bold" style={{ color: platform.color }}>
                        {platform.name[0]}
                      </span>
                    </div>
                    <p className="font-semibold text-slate-900">{platform.name}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {platform.disputeWindow} dispute window
                    </p>
                    {state.selectedPlatforms.includes(platform.id) && (
                      <CheckCircleIcon className="w-5 h-5 text-amber-500 absolute top-3 right-3" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Connect Platforms */}
          {state.step === 2 && (
            <div className="space-y-6">
              {state.selectedPlatforms.map((platformId) => {
                const platform = PLATFORMS.find((p) => p.id === platformId)!;
                const file = state.uploadedFiles[platformId];
                const showCsv = showCsvUpload[platformId];

                return (
                  <div key={platformId} className="border border-slate-200 rounded-xl p-5">
                    {/* Platform Header */}
                    <div className="flex items-center gap-3 mb-5">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${platform.color}15` }}
                      >
                        <span className="text-xl font-bold" style={{ color: platform.color }}>
                          {platform.name[0]}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 text-lg">{platform.name}</h3>
                        <p className="text-xs text-slate-500">{platform.portalUrl}</p>
                      </div>
                    </div>

                    {/* OAuth Connection - Primary Option */}
                    {platform.oauthAvailable ? (
                      <button
                        onClick={() => handleOAuthConnect(platformId)}
                        disabled={oauthConnecting === platformId}
                        className="w-full flex items-center justify-center gap-3 py-4 px-6 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold rounded-xl shadow-lg shadow-amber-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {oauthConnecting === platformId ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          <>
                            <LinkIcon className="w-5 h-5" />
                            Connect with {platform.name}
                          </>
                        )}
                      </button>
                    ) : platformId === 'grubhub' ? (
                      <div className="w-full flex items-center justify-center gap-3 py-4 px-6 bg-slate-100 text-slate-400 font-medium rounded-xl cursor-not-allowed">
                        <ExclamationTriangleIcon className="w-5 h-5" />
                        Requires Business Partnership
                      </div>
                    ) : (
                      <div className="w-full flex items-center justify-center gap-3 py-4 px-6 bg-slate-100 text-slate-500 font-medium rounded-xl">
                        <LinkIcon className="w-5 h-5" />
                        OAuth Coming Soon
                      </div>
                    )}

                    {/* Divider */}
                    <div className="flex items-center gap-4 my-5">
                      <div className="flex-1 h-px bg-slate-200" />
                      <span className="text-xs text-slate-400 font-medium">OR</span>
                      <div className="flex-1 h-px bg-slate-200" />
                    </div>

                    {/* CSV Upload - Secondary Option */}
                    <button
                      onClick={() => setShowCsvUpload(prev => ({ ...prev, [platformId]: !prev[platformId] }))}
                      className="w-full flex items-center justify-between py-3 px-4 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <ArrowUpTrayIcon className="w-4 h-4" />
                        Upload CSV manually
                      </span>
                      <ChevronDownIcon className={clsx('w-4 h-4 transition-transform', showCsv && 'rotate-180')} />
                    </button>

                    {/* CSV Upload Panel */}
                    {showCsv && (
                      <div className="mt-4 pt-4 border-t border-slate-100 space-y-4">
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs font-medium text-slate-700 mb-2">How to export:</p>
                          <ol className="text-xs text-slate-600 space-y-1">
                            {platform.instructions.map((step, idx) => (
                              <li key={idx} className="flex gap-2">
                                <span className="text-slate-400">{idx + 1}.</span>
                                {step}
                              </li>
                            ))}
                          </ol>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">Upload CSV file</span>
                          <button
                            onClick={() => downloadTemplate(platformId)}
                            className="text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1"
                          >
                            <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                            Download Template
                          </button>
                        </div>

                        <input
                          ref={fileInputRefs[platformId]}
                          type="file"
                          accept=".csv"
                          onChange={(e) => handleFileUpload(platformId, e.target.files?.[0] || null)}
                          className="hidden"
                        />
                        <button
                          onClick={() => fileInputRefs[platformId].current?.click()}
                          className={clsx(
                            'w-full p-4 border-2 border-dashed rounded-xl transition-colors',
                            file
                              ? 'border-emerald-300 bg-emerald-50'
                              : 'border-slate-200 hover:border-amber-300 hover:bg-amber-50/50'
                          )}
                        >
                          {file ? (
                            <div className="flex items-center justify-center gap-3">
                              <DocumentTextIcon className="w-6 h-6 text-emerald-500" />
                              <div className="text-left">
                                <p className="font-medium text-slate-900 text-sm">{file.name}</p>
                                <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
                              </div>
                              <CheckCircleIcon className="w-5 h-5 text-emerald-500 ml-auto" />
                            </div>
                          ) : (
                            <div className="text-center">
                              <ArrowUpTrayIcon className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                              <p className="text-sm text-slate-600">Click to upload CSV</p>
                            </div>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Step 3: Review (shows parsed stores) */}
          {state.step === 3 && (
            <div className="space-y-4">
              <p className="text-slate-600">
                Review the stores and orders we found in your uploaded files.
              </p>
              {state.parsedStores.length > 0 ? (
                <div className="space-y-3">
                  {state.parsedStores.map((store, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <BuildingStorefrontIcon className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="font-medium text-slate-900">{store.name}</p>
                          <p className="text-xs text-slate-500 capitalize">{store.platform}</p>
                        </div>
                      </div>
                      <span className="text-sm text-slate-600">{store.orderCount} orders</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-slate-500">Processing your files...</p>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Complete */}
          {state.step === 4 && state.importResults && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon className="w-10 h-10 text-emerald-500" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Setup Complete!</h3>
              <p className="text-slate-600 mb-6">
                Your platforms are connected and ready to help you recover refunds.
              </p>

              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-2xl font-bold text-slate-900">{state.importResults.storesCreated}</p>
                  <p className="text-xs text-slate-500">Stores Created</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-2xl font-bold text-slate-900">{state.importResults.ordersImported}</p>
                  <p className="text-xs text-slate-500">Orders Imported</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-xl">
                  <p className="text-2xl font-bold text-amber-600">
                    ${state.importResults.atRiskAmount.toFixed(2)}
                  </p>
                  <p className="text-xs text-slate-500">At Risk</p>
                </div>
              </div>

              <button
                onClick={onComplete}
                className="px-6 py-3 bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-xl font-medium hover:from-amber-700 hover:to-amber-600 shadow-lg shadow-amber-500/30"
              >
                Go to Disputes Queue
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {state.step < 4 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
            <button
              onClick={prevStep}
              disabled={state.step === 1}
              className="inline-flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back
            </button>
            <button
              onClick={nextStep}
              disabled={!canProceed() || processing}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-xl font-medium hover:from-amber-700 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-amber-500/30"
            >
              {processing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {state.step === 2 ? 'Import Data' : 'Continue'}
                  <ArrowRightIcon className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
